/**
 * useSpecRender — 명세를 고칠 때마다 서버에 그려 달라고 보내고 미리보기를 갈아 끼운다.
 *
 * 계약 §2: POST /api/v2/figspec/render 는 LLM 도 DB 도 쓰지 않고 수 ms 다. 그래서 타자마다
 * 불러도 되지만, 그래도 200ms 는 모아 보낸다(권장값).
 *
 * ⚠ 이 훅이 존재하는 진짜 이유는 디바운스가 아니라 **순서 보장**이다. 요청 A(느림)와
 * B(빠름)가 겹치면 B 가 먼저 돌아오고 A 가 나중에 덮는다. 그러면 화면에 **한 번 전 명세의
 * 그림**이 남고, 저장 잠금이 그 옛 판정으로 풀린다 — 사용자는 무효한 명세를 저장할 수 있게 된다.
 * 그래서 순번(seq)을 매겨 **마지막으로 보낸 것의 응답만** 받는다.
 *
 * 반환 { pending, result, error }
 *  - result: { ok, svg, svgText, width, height, alt, issues[] } | null
 *  - ok:false 는 **오류가 아니다**(계약 §2 — 무효 명세도 200 이다). error 는 통신·400 뿐이다.
 */
import { useEffect, useRef, useState } from 'react';
import { renderFigspec, AUTH_REDIRECT } from './figureApi';

/**
 * 응답을 받아들일지 판정한다. 순수 함수라 따로 검사한다.
 * 늦게 온 옛 응답(seq < latest)은 버린다. 같은 번호면 받는다.
 */
export function acceptRenderResponse(seq, latestSeq) {
  return seq === latestSeq;
}

/** 명세로 쓸 수 있는 값인가 — 객체여야 하고 배열·null 은 아니다. */
export function isRenderableSpec(spec) {
  return spec != null && typeof spec === 'object' && !Array.isArray(spec);
}

export default function useSpecRender(spec, getAccessToken, { delay = 200, enabled = true } = {}) {
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const seqRef = useRef(0);
  const tokenRef = useRef(getAccessToken);
  tokenRef.current = getAccessToken;

  // 명세를 문자열로 굳혀 의존성으로 쓴다 — 객체 참조로 걸면 렌더마다 새 요청이 나간다.
  const key = isRenderableSpec(spec) ? safeStringify(spec) : null;

  useEffect(() => {
    if (!enabled || key == null) {
      setPending(false);
      return undefined;
    }
    const seq = (seqRef.current += 1);
    setPending(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const d = await renderFigspec(JSON.parse(key), tokenRef.current);
        if (d === AUTH_REDIRECT) return;
        if (!acceptRenderResponse(seq, seqRef.current)) return; // 늦게 온 옛 응답 — 버린다
        setResult({
          ok: d?.ok === true,
          svg: d?.svg ?? null,
          svgText: d?.svg_text ?? null,
          width: d?.width ?? null,
          height: d?.height ?? null,
          alt: d?.alt ?? '',
          issues: Array.isArray(d?.issues) ? d.issues : [],
        });
      } catch (e) {
        if (!acceptRenderResponse(seq, seqRef.current)) return;
        // 여기 오는 것은 통신 오류나 400(spec 키 없음)뿐이다 — 무효 명세는 200 ok:false 로 온다.
        setError(e.message || '미리보기를 그리지 못했습니다.');
        setResult(null);
      } finally {
        if (acceptRenderResponse(seq, seqRef.current)) setPending(false);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [key, delay, enabled]);

  return { pending, result, error };
}

function safeStringify(v) {
  try { return JSON.stringify(v); } catch (_) { return null; }
}
